import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { ChevronIcon, CloseIcon, UpdateIcon } from '../../bombiix-icon';
import { abortUpload } from '../../utils/active-uploads-registry';
import { resumeSupportingUpload, discardResumableUpload, fetchAllResumableUploads } from '../../Pages/DevlopmentOrder/store/DevelopmentOrder.action';
import { resumeBomSupportingUpload, discardBomResumableUpload, fetchAllBomResumableUploads } from '../SupportingDocument/store/SupportingDocument.action';
import { resumeAssetUpload, discardResumableUpload as discardAssetResumableUpload } from '../../Pages/AssetLibrary/Assets/store/asset.actions';
import { localStorageService } from '../../services/localStorage.service';
import uploaderType from '../../Pages/UploaderRedux/store/uploader.type';
import { requestLiveUploads, onUploadCompletedElsewhere } from '../../utils/upload-tab-coordinator';

const BOM_SUPPORTING_DOC_MODULE = 'bom-supporting-doc';
const ASSET_LIBRARY_MODULE = 'asset-library';
/** Wait for sibling tabs to answer the who-is-live query before listing resumable uploads, so an upload still running in another tab is not shown here as failed. */
const LIVE_UPLOAD_DISCOVERY_DELAY_MS = 400;
/** Percent at or above which the progress bar switches from amber to green (Figma spec). */
const PROGRESS_COMPLETE_THRESHOLD = 60;

/** Icon sizes fixed by the uploader Figma spec (chevron/close 16px glyph, retry refresh 14px). */
const TAB_ICON_SIZE = 16;
const CANCEL_ICON_SIZE = 16;
const RETRY_ICON_SIZE = 14;

/**
 * Derives an accept filter (e.g. ".pdf") from a stored file name so the resume
 * re-pick dialog only offers the matching type.
 * @param {string} fileName - Original file name from the pending record.
 * @returns {string} Accept attribute value, or empty string when unknown.
 */
function acceptFromFileName(fileName) {
  const parts = (fileName || '').split('.');
  return parts.length > 1 ? `.${parts.pop().toLowerCase()}` : '';
}

/** Maps a pending-upload module key to its i18n label key so the widget can show where a resumable upload belongs. */
const MODULE_LABEL_KEY = {
  'design-supporting-doc': 'modules/design-detail:MODULE_DESIGN_SUPPORTING_DOC',
  'bom-supporting-doc': 'modules/design-detail:MODULE_BOM_SUPPORTING_DOC',
  'asset-library': 'modules/design-detail:MODULE_ASSET_LIBRARY',
};

/**
 * Builds the context line under a file name: module label + product number.
 * @param {Object} file - Uploader row.
 * @param {Function} t - Translator.
 * @returns {string} The context string.
 */
function contextLine(file, t) {
  const moduleLabel = file?.module && MODULE_LABEL_KEY[file.module] ? t(MODULE_LABEL_KEY[file.module]) : '';
  const product = file?.productNumber ? ` · ${file.productNumber}` : '';
  return `${moduleLabel}${product}`;
}

/**
 * Failed/resume row body — status text plus Retry and Discard actions.
 */
function FailedRow({ file, onRetry, onDiscard, t }) {
  return (
    <div className="bombiix-uploader__status-row">
      <p className="bombiix-uploader__status">{t('modules/design-detail:UPLOAD_FAILED')}</p>
      <div className="bombiix-uploader__actions">
        <button type="button" className="bombiix-uploader__retry" onClick={() => onRetry(file)}>
          <span className="bombiix-uploader__retry-icon">
            <UpdateIcon size={RETRY_ICON_SIZE} />
          </span>
          <span className="bombiix-uploader__retry-label">{t('modules/design-detail:RETRY')}</span>
        </button>
        <button type="button" className="bombiix-uploader__discard" onClick={() => onDiscard(file)}>
          {t('modules/design-detail:DISCARD')}
        </button>
      </div>
    </div>
  );
}

FailedRow.propTypes = {
  file: PropTypes.shape({}).isRequired,
  onRetry: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

/**
 * In-progress row body — gradient progress bar plus optional cropped/thumbnail
 * post-processing status lines (DAM image/video flow).
 */
function ProgressRow({ file, t }) {
  const percentage = file?.percentage || 0;
  const progressClass = `bombiix-uploader__progress${percentage >= PROGRESS_COMPLETE_THRESHOLD ? ' bombiix-uploader__progress--complete' : ''}`;
  return (
    <>
      <div className={progressClass} style={{ '--progress': `${percentage}%` }}>
        {percentage > 0 ? `${percentage}%` : t('DEVLOPMENT_ORDER.PREVIEW_MODAL.INITIATING_UPLOAD')}
      </div>
      {file?.isCroppedImageLoader && <p className="bombiix-uploader__status">{t('DEVLOPMENT_ORDER.PREVIEW_MODAL.UPLOADING_CROPPED_IMAGE')}</p>}
      {file?.isThumbnailImageLoader && <p className="bombiix-uploader__status">{t('DEVLOPMENT_ORDER.PREVIEW_MODAL.UPLOADING_VIDEO_THUMBNAIL')}</p>}
    </>
  );
}

ProgressRow.propTypes = {
  file: PropTypes.shape({}).isRequired,
  t: PropTypes.func.isRequired,
};

/**
 * Floating card reporting every upload pushed onto the global uploader queue: one progress bar per
 * in-flight file, plus a `done / total` counter for callers that publish a batch summary.
 *
 * It also owns the resume prompt. On mount it asks the backend for uploads the user left
 * unfinished in an earlier session and renders each as a Resume / Discard row — which is why the
 * prompt appears on whatever page the user happens to land on after logging back in.
 */
function ProgressbarUploader() {
  const [uploaderFiles, setUploaderFiles] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const reducer = useSelector((reducerstate) => reducerstate);
  const { uploader } = reducer;
  const { queueFiles } = uploader;
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const repickInputRef = useRef(null);
  const resumeTargetRef = useRef(null);

  useEffect(() => {
    if (queueFiles) {
      setUploaderFiles(queueFiles);
    }
  }, [queueFiles]);

  useEffect(() => {
    if (!localStorageService.get('loggedInUserInfo')) {
      return undefined;
    }
    requestLiveUploads();
    const fetchTimer = setTimeout(() => {
      dispatch(fetchAllResumableUploads());
      dispatch(fetchAllBomResumableUploads());
    }, LIVE_UPLOAD_DISCOVERY_DELAY_MS);
    const unsubscribe = onUploadCompletedElsewhere((taskId) => {
      dispatch({ type: uploaderType.REMOVE_TASK_FROM_LIST, payload: taskId });
    });
    return () => {
      clearTimeout(fetchTimer);
      unsubscribe();
    };
  }, [dispatch]);

  const onResumeClick = (file) => {
    resumeTargetRef.current = file;
    if (repickInputRef.current) {
      repickInputRef.current.value = '';
      repickInputRef.current.accept = acceptFromFileName(file?.fileName);
      repickInputRef.current.click();
    }
  };

  const onRepicked = (event) => {
    const repickedFile = event.target.files && event.target.files[0];
    const target = resumeTargetRef.current;
    if (repickedFile && target) {
      if (target.module === ASSET_LIBRARY_MODULE) {
        dispatch(resumeAssetUpload(target, repickedFile));
      } else if (target.module === BOM_SUPPORTING_DOC_MODULE) {
        dispatch(resumeBomSupportingUpload(target, repickedFile, t));
      } else {
        dispatch(resumeSupportingUpload(target, repickedFile, t));
      }
    }
    resumeTargetRef.current = null;
  };

  const onDiscardClick = (file) => {
    if (file?.module === ASSET_LIBRARY_MODULE) {
      dispatch(discardAssetResumableUpload(file?.taskId));
    } else if (file?.module === BOM_SUPPORTING_DOC_MODULE) {
      dispatch(discardBomResumableUpload(file?.taskId));
    } else {
      dispatch(discardResumableUpload(file?.devOrderId, file?.taskId));
    }
  };

  const fileKeys = uploaderFiles ? Object.keys(uploaderFiles) : [];
  const hasFailed = fileKeys.some((key) => uploaderFiles[key]?.status === 'resumable');

  const rootClass = ['bombiix-uploader', hasFailed && 'bombiix-uploader--failed', collapsed && 'bombiix-uploader--collapsed'].filter(Boolean).join(' ');
  const tabLabel = hasFailed ? t('modules/design-detail:UPLOAD_FAILED') : t('modules/design-detail:UPLOAD_IN_PROGRESS');

  return (
    !isEmpty(uploaderFiles) && (
      <div className={rootClass}>
        <input ref={repickInputRef} type="file" className="d-none" onChange={onRepicked} aria-hidden="true" />
        <button type="button" className="bombiix-uploader__tab" onClick={() => setCollapsed((prev) => !prev)}>
          <span className="bombiix-uploader__tab-chevron">
            <ChevronIcon size={TAB_ICON_SIZE} />
          </span>
          <span className="bombiix-uploader__tab-label">{tabLabel}</span>
        </button>

        <div className="bombiix-uploader__panel">
          <div className="bombiix-uploader__header">{t('UPLOAD_FILE_AND_DOCUMENT.UPLOADING')}</div>
          <div className="bombiix-uploader__body">
            {fileKeys.map((key) => {
              const file = uploaderFiles[key];
              const canCancel = file?.cancellable && !file?.completed;
              return (
                <div key={key} className="bombiix-uploader__row">
                  <div className="bombiix-uploader__row-head">
                    <div className="bombiix-uploader__meta">
                      <p className="bombiix-uploader__file-name">{file?.fileName}</p>
                      <p className="bombiix-uploader__context">{contextLine(file, t)}</p>
                    </div>
                    {canCancel && (
                      <button type="button" aria-label="cancel" className="bombiix-uploader__cancel" onClick={() => abortUpload(file?.taskId)}>
                        <CloseIcon size={CANCEL_ICON_SIZE} />
                      </button>
                    )}
                  </div>

                  {file?.status === 'resumable' ? (
                    <FailedRow file={file} onRetry={onResumeClick} onDiscard={onDiscardClick} t={t} />
                  ) : (
                    <ProgressRow file={file} t={t} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    )
  );
}
export default ProgressbarUploader;
